/**
 * Response models for analytics API
 */

import { AnalyticsEvent } from './analytics-event.model';

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  statusCode?: number;
}

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalEvents: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * Paginated events response
 */
export interface PaginatedEventsResponse {
  success: boolean;
  data: {
    events: AnalyticsEvent[];
    pagination: PaginationMeta;
    filters?: any;
    organizationId?: string;
  };
}

/**
 * Overview statistics
 */
export interface OverviewStats {
  totalEvents: number;
  eventsByCategory: Record<string, number>;
  eventsBySeverity: Record<string, number>;
  topEventTypes: Array<{ eventType: string; count: number }>;
  activeUsers: number;
  activeOrganizations?: number;
  errorRate?: number;
}

/**
 * Platform overview response
 */
export interface PlatformOverviewResponse {
  success: boolean;
  data: {
    timeRange: string;
    statistics: OverviewStats;
  };
}

/**
 * Organization overview response
 */
export interface OrganizationOverviewResponse {
  success: boolean;
  data: {
    organizationId: string;
    timeRange: string;
    statistics: OverviewStats;
  };
}

/**
 * Organization activity metrics
 */
export interface OrganizationActivity {
  orgId: string;
  orgName?: string;
  totalEvents: number;
  activeUsers: number;
  errorCount: number;
  lastActivity: string;
  topEventTypes: Array<{ eventType: string; count: number }>;
}

/**
 * Organizations activity response
 */
export interface OrganizationsActivityResponse {
  success: boolean;
  data: {
    timeRange?: string;
    totalOrganizations: number;
    organizations: OrganizationActivity[];
  };
}

/**
 * Security event details
 */
export interface SecurityEvent {
  eventId: string;
  eventType: string;
  severity: string;
  timestamp: string;
  userId?: string;
  userEmail?: string;
  orgId?: string;
  description: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
}

/**
 * Security events summary
 */
export interface SecurityEventsSummary {
  totalEvents: number;
  criticalEvents: number;
  errorEvents: number;
  warningEvents: number;
  suspiciousIPs: string[];
  affectedUsers: number;
  affectedOrganizations: number;
}

/**
 * Security events response
 */
export interface SecurityEventsResponse {
  success: boolean;
  data: {
    timeRange: string;
    summary: SecurityEventsSummary;
    events: SecurityEvent[];
  };
}

/**
 * Struggling user details
 */
export interface StrugglingUser {
  userId: string;
  userEmail?: string;
  userName?: string;
  orgId?: string;
  errorCount: number;
  warningCount?: number;
  lastError: string;
  errorTypes?: string[];
  recentErrors?: { eventType: string; timestamp: string; endpoint?: string }[];
  affectedFeatures: string[];
}

/**
 * Struggling users response
 */
export interface StrugglingUsersResponse {
  success: boolean;
  data: {
    timeRange?: string;
    totalCount: number;
    strugglingUsers: StrugglingUser[];
    message?: string;
  };
}

/**
 * Performance metrics
 */
export interface PerformanceMetrics {
  totalRequests: number;
  averageResponseTime: number;
  slowestEndpoints: Array<{
    endpoint: string;
    method: string;
    averageTime: number;
    requestCount: number;
  }>;
  errorRate: number;
  statusCodeDistribution: Record<string, number>;
}

/**
 * Performance metrics response
 */
export interface PerformanceMetricsResponse {
  success: boolean;
  data: {
    timeRange: string;
    metrics: PerformanceMetrics;
  };
}

/**
 * User activity summary
 */
export interface UserActivity {
  userId: string;
  userEmail?: string;
  userName?: string;
  totalActions: number;
  lastActivity: string;
  topActions: Array<{ action: string; count: number }>;
  errorCount?: number;
}

/**
 * User activity response
 */
export interface UserActivityResponse {
  success: boolean;
  data: {
    organizationId: string;
    timeRange: string;
    totalUsers: number;
    users: UserActivity[];
  };
}

/**
 * Specific user activity response
 */
export interface SpecificUserActivityResponse {
  success: boolean;
  data: {
    userId: string;
    userEmail?: string;
    userName?: string;
    organizationId: string;
    timeRange: string;
    totalEvents: number;
    eventsByType: Record<string, number>;
    recentEvents: AnalyticsEvent[];
  };
}

/**
 * Audit trail response
 */
export interface AuditTrailResponse {
  success: boolean;
  data: {
    resourceType: string;
    resourceId: string;
    totalEvents: number;
    events: AnalyticsEvent[];
  };
}
